import { Trans } from 'lingui-react';
import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { compose, setDisplayName } from 'recompose';
import { debounce, get, pick, noop, isNil } from 'lodash';

import { px2rem } from 'decisiv-ui-utils';
import { Accordion, Divider, MessageSmall } from 'base-components';
import { Row, Column } from 'styled-components-reactive-grid';

import Panel from 'blocks/Panel';

import {
  withCasePanelStatusActions,
  CASE_STATUS,
  CASE_PANELS,
  PANEL_STATUSES,
} from 'compositions/CaseStatus';
import DealerLocator from 'compositions/DealerLocator';

import getTravelEstimate from 'utils/travelEstimate';
import withPatternValidationOnKeyUp from 'utils/withPatternValidationOnKeyUp';
import { useFocusReceiver } from 'setup/FocusProvider';

import { invalidCharsForExporting } from 'constants/index';

import AssetLocationNotesInput from './AssetLocationNotesInput';
import withAssetLocationSearch from './withAssetLocationSearch';
import withCaseInboundProgramNumber from './withCaseInboundProgramNumber';
import withCreateDealerResponseMutation from './withCreateDealerResponseMutation';
import CaseDealerLocatorExtraInputs from './ExtraInputs';
import withUpdateProximityLevelMutation from './withUpdateProximityLevelMutation';

import { fieldIds } from './constants';
import { reportAssetLocationStatus, getDealerDataTransformer } from './utils';

export class CaseDealerLocatorPanel extends Component {
  state = {
    searchResultsExpanded: false,
    errors: { assetLocation: false, notes: false },
  };

  componentDidUpdate(prevProps) {
    const assetLocation = get(this.props, 'caseDetail.assetLocation', {});

    reportAssetLocationStatus({ ...this.props, assetLocation });
  }

  onExpandedChange = ({ expanded }) => {
    this.setState({ searchResultsExpanded: !expanded });
  };

  createResponseForOutboundCall = ({ phoneNumber, dealer, dealerContact }) => {
    const { caseDetail, createDealerResponse } = this.props;

    const {
      assetLocationDriveDistance,
      assetLocationDriveTime,
    } = getTravelEstimate(get(dealer, 'travelEstimate'));

    if (caseDetail.status === CASE_STATUS.dispatch) {
      createDealerResponse({
        caseId: caseDetail.id,
        dealerId: dealer.id,
        contactPerson: get(dealerContact, 'name'),
        phoneNumber,
        dealerType: 'DEALER', // the Asset Location panel only has "real dealers"
        assetLocationDriveDistance,
        assetLocationDriveTime,
      });
    }
  };

  buildInitialAssetLocation = () => {
    const { assetLocation } = this.props.caseDetail;
    const coordinates = get(assetLocation, 'coordinates');
    const location =
      coordinates && pick(assetLocation.coordinates, ['latitude', 'longitude']);

    return {
      // These values are null if there is no asset location, which doesn't trigger the default
      // value arg of `get`, but is falsy.
      countryCode: get(assetLocation, 'countryCode') || '',
      location: location || undefined,
      searchValue: get(assetLocation, 'searchValue') || '',
      searchValueTried: get(assetLocation, 'searchValue') || '',
      addressComponents: get(assetLocation, 'address'),
    };
  };

  handleUpdateAssetLocation = ({
    countryCode,
    location,
    searchValue,
    addressComponents,
  }) => {
    const { hasInvalidPattern } = this.props;

    if (!searchValue || !hasInvalidPattern('assetLocation', searchValue)) {
      this.props.updateCase({
        variables: {
          countryCode: countryCode || '',
          id: this.props.caseDetail.id,
          location: location || null,
          searchValue: searchValue || '',
          addressComponents,
        },
      });

      if (get(this.state, 'errors.assetLocation')) {
        this.setState({
          errors: {
            ...this.state.errors,
            assetLocation: false,
          },
        });
      }
    }
  };

  updateAssetLocation = debounce(this.handleUpdateAssetLocation, 1000, {
    trailing: true,
  });

  handleBlur = name => value => {
    const { onValidatingFieldBlur, hasInvalidPattern } = this.props;
    onValidatingFieldBlur(name);

    this.setState({
      errors: {
        ...this.state.errors,
        [name]: hasInvalidPattern(name, value),
      },
    });
  };

  handleKeyUp = name => event => {
    const { onValidatingFieldKeyUp, hasInvalidPattern } = this.props;
    onValidatingFieldKeyUp(name, event);

    this.setState({
      errors: {
        ...this.state.errors,
        [name]: hasInvalidPattern(name, get(event, 'target.value')),
      },
    });
  };

  buildSearchResults = () => {
    const { caseDetail, casePanelStatuses } = this.props;
    const panelStatus = get(casePanelStatuses, 'assetLocation.status.panel');

    if (caseDetail.status === CASE_STATUS.new) {
      if (panelStatus === PANEL_STATUSES.complete) {
        return (
          <Row>
            <Column modifiers={['col', 'padScaleX_3']}>
              <Accordion
                defaultExpanded={false}
                onExpandedChange={this.onExpandedChange}
              >
                <Accordion.Head
                  style={{ height: 'auto', alignItems: 'baseline' }}
                >
                  <Accordion.Title
                    style={{ textTransform: 'none', width: '100%' }}
                  >
                    <Row>
                      <Column modifiers={['col', 'padScale_0']}>
                        <DealerLocator.Legend
                          isExpanded={this.state.searchResultsExpanded}
                        />
                      </Column>
                    </Row>
                  </Accordion.Title>
                </Accordion.Head>
                <Accordion.Body allowOverflowWhenOpen>
                  <Fragment>
                    <Row style={{ padding: `0 ${px2rem(5)}` }}>
                      <Column modifiers={['col', 'padScale_0']}>
                        <DealerLocator.SearchFilters />
                      </Column>
                    </Row>
                    <Divider />
                    <Row style={{ height: px2rem(300) }}>
                      <Column modifiers={['col', 'padScale_0']}>
                        <DealerLocator.SearchResultsList
                          onOutboundCall={this.createResponseForOutboundCall}
                        />
                      </Column>
                    </Row>
                  </Fragment>
                </Accordion.Body>
              </Accordion>
            </Column>
          </Row>
        );
      }
      return (
        <Row>
          <Column modifiers={['col', 'padScaleX_3', 'padScaleY_0']}>
            <DealerLocator.Legend />
          </Column>
        </Row>
      );
    }

    return (
      <Fragment>
        <Row>
          <Column modifiers={['col', 'padScaleX_3', 'padScaleY_0']}>
            <DealerLocator.Legend />
          </Column>
        </Row>
        <Row>
          <Column modifiers={['col', 'padScaleX_3', 'padScaleY_0']}>
            <DealerLocator.SearchFilters />
          </Column>
        </Row>
        <Divider />
        <Row style={{ height: px2rem(300) }}>
          <Column modifiers={['col', 'padScale_0']}>
            <DealerLocator.SearchResultsList
              onOutboundCall={this.createResponseForOutboundCall}
            />
          </Column>
        </Row>
      </Fragment>
    );
  };

  renderValidationError = name => {
    const { showValidationError } = this.props;
    const { errors } = this.state;

    if (!showValidationError(name) || !errors[name]) return null;

    const field = {
      assetLocation: 'location',
      notes: 'note',
    }[name];

    return (
      <Row>
        <Column modifiers={['col', 'padScaleY_2']}>
          <MessageSmall type="warning">
            <Trans>
              The {field} cannot contain the following characters: ~`
            </Trans>
          </MessageSmall>
        </Column>
      </Row>
    );
  };

  render() {
    const {
      caseDetail,
      inboundProgramNumber,
      loading,
      hasInvalidPattern,
      readOnly,
      onFocusRequested,
      updateProximityLevel,
    } = this.props;
    const dealerDataTransformer = getDealerDataTransformer(this.props);

    return (
      <Panel modifiers="padScale_0">
        <DealerLocator
          key={caseDetail.id || 'case-not-loaded'}
          initialAssetLocation={this.buildInitialAssetLocation()}
          inboundProgramNumber={inboundProgramNumber}
          dealerDataTransformer={dealerDataTransformer}
          searchAreaDistanceFilterIndex={
            !isNil(caseDetail.proximityLevel)
              ? String(caseDetail.proximityLevel)
              : undefined
          }
          updateSearchAreaDistanceFilterIndex={proximityLevel =>
            updateProximityLevel({
              caseId: caseDetail.id,
              proximityLevel: Number(proximityLevel),
            })
          }
        >
          <Row>
            <Column modifiers={['col', 'padScale_0']}>
              <DealerLocator.SearchInput
                assetLocationOnly
                onAssetLocationChange={this.updateAssetLocation}
                onAssetLocationKeyUp={this.handleKeyUp('assetLocation')}
                onAssetLocationBlur={this.handleBlur('assetLocation')}
                assetLocationValidationError={this.renderValidationError(
                  'assetLocation',
                )}
                extraInputsRight={
                  <CaseDealerLocatorExtraInputs
                    caseDetail={caseDetail}
                    loading={loading}
                    readOnly={readOnly}
                  />
                }
                extraInputsBottom={
                  !loading && (
                    <AssetLocationNotesInput
                      caseDetail={caseDetail}
                      readOnly={readOnly}
                      onKeyUp={this.handleKeyUp('notes')}
                      onBlur={this.handleBlur('notes')}
                      validationError={this.renderValidationError('notes')}
                      hasInvalidPattern={value =>
                        hasInvalidPattern('notes', value)
                      }
                    />
                  )
                }
                readOnly={readOnly}
                textFieldRef={onFocusRequested}
              />
            </Column>
          </Row>
          <Row>
            <Column
              modifiers={['col', 'padScale_0']}
              style={{
                height: px2rem(365),
                overflow: 'hidden',
              }}
            >
              <DealerLocator.SearchResultsMap
                onAssetLocationChange={this.updateAssetLocation}
                readOnly={readOnly}
              />
            </Column>
          </Row>
          {this.buildSearchResults()}
        </DealerLocator>
      </Panel>
    );
  }
}

CaseDealerLocatorPanel.propTypes = {
  caseDetail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    assetLocation: PropTypes.shape({
      coordinates: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
      }),
    }),
  }),
  casePanelStatuses: PropTypes.shape({
    assetLocation: PropTypes.shape({
      label: PropTypes.string,
      status: PropTypes.shape({
        panel: PropTypes.string,
      }),
    }),
  }),
  updateProximityLevel: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  caseNumber: PropTypes.string.isRequired,
  inboundProgramNumber: PropTypes.shape({
    billTo: PropTypes.string.isRequired,
  }),
  loading: PropTypes.bool.isRequired,
  updateCase: PropTypes.func.isRequired,

  // from withCreateDealerResponseMutation:
  createDealerResponse: PropTypes.func.isRequired,

  // from withPatternValidationOnKeyUp
  hasInvalidPattern: PropTypes.func,
  onValidatingFieldBlur: PropTypes.func,
  onValidatingFieldKeyUp: PropTypes.func,
  showValidationError: PropTypes.func,
  readOnly: PropTypes.bool,
  onFocusRequested: PropTypes.func.isRequired,
};

CaseDealerLocatorPanel.defaultProps = {
  caseDetail: {},
  casePanelStatuses: {},
  inboundProgramNumber: undefined,
  hasInvalidPattern: noop,
  onValidatingFieldBlur: noop,
  onValidatingFieldKeyUp: noop,
  showValidationError: noop,
  readOnly: false,
  updateProximityLevel: undefined,
};

export default compose(
  setDisplayName('CaseDealerLocatorPanel'),
  withCasePanelStatusActions(CASE_PANELS.assetLocation),
  withAssetLocationSearch,
  withCreateDealerResponseMutation,
  withCaseInboundProgramNumber,
  withUpdateProximityLevelMutation,
  withPatternValidationOnKeyUp({
    fields: {
      assetLocation: invalidCharsForExporting,
      notes: invalidCharsForExporting,
    },
  }),
  useFocusReceiver(fieldIds.location),
)(CaseDealerLocatorPanel);



// WEBPACK FOOTER //
// ./src/compositions/CaseDealerLocatorPanel/index.js